import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Switch, Route, useRouteMatch, Redirect } from 'react-router-dom';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import {
  ListMerchCatalog,
  UpdateMerch,
} from '@webapp/store/merch/action-creators';
import { MerchState } from '@webapp/store/merch/state';
import { LIST_MERCH_CATALOG } from '@webapp/store/merch/actions';
import { createMerchRequest } from '@webapp/store/merch/selectors';
import { ListReleases } from '@webapp/store/release/action-creators';
import { hasMerchSubscription } from '@webapp/store/subscription/selectors';
import { LIST_SUBSCRIPTIONS } from '@webapp/store/subscription-manager/actions';

import { MerchProduct } from '@services/merch/types/api/merch-product';
import { CreateProductRequest } from '@services/merch/types/api/create-product';
import { UpdateProductRequest } from '@services/merch/types/api/update-product';

import SimpleArtist from '@common/types/artist/simple-artist';

import FormContainer from '@components/layout/form-container';
import SidebarWrapper from '@components/layout/sidebar-wrapper';
import SidebarContainer from '@components/layout/sidebar-container';

import MerchSidebarCtrl from '@webapp/controllers/merch/merch-sidebar';

import MerchDesignPage from './design';
import MerchColorSizePage from './color-size';
import MerchSelectProductPage from './select-product';
import MerchPricingOwnersPage from './pricing-owners';
import MerchMenuMobile from '@webapp/controllers/merch/merch-menu-mobile';
import { Translate } from 'react-localize-redux';
import SmallNotification from '@components/small-notification';

interface MerchPageProps {
  className?: string;
  catalog: MerchState['catalog'];
  activeMerch: MerchState['active'];
  artist?: SimpleArtist;
  isEdit?: boolean;
  isMobile?: boolean;
}

interface MerchStateProps {
  productId?: string;
  request: CreateProductRequest | UpdateProductRequest;
  updateMerch: (
    data: UpdateProductRequest,
    productId: string,
    artistId: string,
    finish: boolean,
    sampleOrder?: boolean,
    orderId?: string | undefined
  ) => UpdateMerch;
  listCatalog: () => ListMerchCatalog;
  listReleases: () => ListReleases;
  imageId?: string;
  merchEnabled: boolean;
  artistMerch: MerchProduct[];
  loading: boolean;
  loaded: boolean;
  sampleOrder: boolean | undefined;
  orderId: string | undefined;
}

const mapStateToProps = (state: AppState) => ({
  catalog: state.merch.catalog,
  activeMerch: state.merch.active,
  artistMerch: state.merch.artistMerch,
  artist: state.artist.active,
  productId:
    state.merch.active?.editProduct?.id ||
    (state.merch.active?.newProduct?.productId
      ? state.merch.active?.newProduct?.productId
      : state.merch.active?.product
      ? state.merch.active.product?.id.toString()
      : ''),
  imageId:
    (state.merch.active?.image && state.merch.active?.image[0]?.imageId) ||
    state.merch.active?.editProduct?.printify[0]?.imageId,
  request: createMerchRequest(state.merch),
  merchEnabled: hasMerchSubscription(state),
  loading:
    !!state.ui.loading[LIST_SUBSCRIPTIONS] ||
    !!state.ui.loading[LIST_MERCH_CATALOG],
  loaded: state.subscriptionManager.loaded,
  sampleOrder: !!state.merch.active?.productSample?.receiveSample || undefined,
  orderId: state.merch.active?.productSampleOrder?.orderId || undefined,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  listCatalog: () => dispatch(new ListMerchCatalog()),
  listReleases: () => dispatch(new ListReleases('all')),
  updateMerch: (
    data: UpdateProductRequest,
    productId: string,
    artistId: string,
    finish = false,
    sampleOrder = false,
    orderId?: string | undefined
  ) =>
    dispatch(
      new UpdateMerch(data, productId, artistId, finish, sampleOrder, orderId)
    ),
});

const MerchPage = ({
  catalog,
  artist,
  activeMerch,
  listCatalog,
  listReleases,
  updateMerch,
  productId,
  isEdit = false,
  request,
  merchEnabled,
  artistMerch,
  loading,
  loaded,
  sampleOrder,
  orderId,
  isMobile,
  ...props
}: MerchPageProps & MerchStateProps) => {
  const match = useRouteMatch<{ merchId: string }>();
  const location = useLocation();

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );

  React.useEffect(() => {
    if (!isEdit) {
      listCatalog();
    }
  }, [listCatalog, isEdit]);

  React.useEffect(() => {
    if (artist) {
      listReleases();
    }
  }, [artist, listReleases]);

  const isValid = () => {
    return (
      request.imageId &&
      request?.position &&
      request?.itemId &&
      request?.providerId &&
      request?.sizes &&
      request?.title &&
      (!!activeMerch?.colorSize?.colors.length || request?.colors)
    );
  };

  const handleEditProduct = () => {
    const finish = true;
    if (artist && productId && isValid()) {
      updateMerch(
        request as UpdateProductRequest,
        productId,
        artist.id,
        finish,
        sampleOrder,
        orderId
      );
    }
  };

  const usingEditor = !['/merch/subscribe', '/merch/welcome'].includes(
    location.pathname
  );

  const path = match.params.merchId
    ? match.path.replace(':merchId', match.params.merchId)
    : match.path;

  if (loading || !loaded || !artist) {
    return null;
  }

  if (artistMerch.length > 0 && !usingEditor) {
    return <Redirect to="/catalog?type=merch" />;
  }

  return (
    <StyledMerchPage className={props.className}>
      <SidebarContainer>
        <SidebarWrapper>
          <MerchSidebarCtrl isEdit={isEdit} />
        </SidebarWrapper>
        {isMobile && <MerchMenuMobile isEdit={isEdit} />}
        <FormContainer hasSidebar>
          <Switch>
            {!isEdit && (
              <Route
                exact
                path={`${path}/select-product`}
                render={() => <MerchSelectProductPage />}
              />
            )}
            <Route
              exact
              path={`${path}/color-size`}
              render={() => {
                if (!match.params.merchId && !activeMerch) {
                  return <Redirect to={`${path}/select-product`} />;
                }

                if (!catalog.length && !isEdit) {
                  return <Redirect to={path} />;
                }

                return <MerchColorSizePage isEdit={isEdit} />;
              }}
            />
            <Route
              exact
              path={`${path}/design`}
              render={() =>
                (!catalog.length && !isEdit) ||
                !activeMerch ||
                !activeMerch.colorSize ? (
                  <Redirect to={path} />
                ) : (
                  <MerchDesignPage isEdit={isEdit} />
                )
              }
            />
            <Route
              exact
              path={`${path}/pricing-owners`}
              render={() =>
                (!catalog.length && !isEdit) ||
                !activeMerch ||
                !activeMerch.colorSize ? (
                  <Redirect to={path} />
                ) : (
                  <MerchPricingOwnersPage
                    isEdit={isEdit}
                    onNext={handleEditProduct}
                  />
                )
              }
            />
            <Route
              exact
              path={path}
              render={() =>
                isEdit ? (
                  <Redirect to={`${path}/color-size`} />
                ) : (
                  <Redirect to={`${path}/select-product`} />
                )
              }
            />
          </Switch>
        </FormContainer>
      </SidebarContainer>
      {(toastNotification?.value === 'invite_sent' ||
        toastNotification?.value === 'invite_error') && (
        <SmallNotification
          notificationType={toastNotification.type}
          title={<Translate id={toastNotification.label} />}
        />
      )}
    </StyledMerchPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchPage);

const StyledMerchPage = styled.div`
  margin-top: 30px;
  padding-bottom: 200px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    margin-top: 70px;
  }
`;
